import { Button, ToastItem, useIsBreakpointLargeAndUp, useToast } from '@afterpaytouch/core'
import { useTranslation } from '../../utils/convergence'
import React, { useContext, useEffect, useState } from 'react'
import {
  DisabledModal,
  Head,
  BiometricRegistrationModal,
  SummaryNotificationContext,
  DockedFooterPortal,
  NoErrorStatusGuard,
  StartedStatusGuard,
  SummaryRouteGuard,
} from '../../component'
import { ApplePayStatus, CheckoutStatus, LimitedViewElementId, PaymentMethodType, CheckoutPage } from '../../model'
import { NonTerminalErrors, PaymentMethodTypes, TrackingEvent } from '../../model/monitoring'
import {
  addElementView,
  useAppDispatch,
  useApplePayState,
  useCheckoutError,
  useCheckoutState,
  useDownPaymentAmount,
  useEnforcePclCardExpiryLimit,
  useIsReadyToConfirm,
  useLimitedViewElementControl,
  usePreferredCard,
  useSelectedPaymentMethod,
} from '../../state'
import { TrackPageViewed, useMonitoringWithEnduringEventProperties } from '../../integrations/monitoring'
import { usePostCheckoutTriggerExperiment } from '../../utils/post-checkout'
import { FirstPaymentType } from '@afterpaytouch/portal-api'
import { DetailedItemList, Header, PaymentMethod, Summary } from './components'
import { useOnConfirmButtonClick } from './hooks'
import styles from './style.module.css'

export const AppleRetailSummaryPage: CheckoutPage = () => {
  const { notification, setClicked, clicked } = useContext(SummaryNotificationContext)
  const { t } = useTranslation()
  const { status, firstPaymentType, started, error } = useCheckoutState()

  const selectedPaymentMethod = useSelectedPaymentMethod()
  const applepay = useApplePayState()
  const ready = useIsReadyToConfirm()
  const { logEvent } = useMonitoringWithEnduringEventProperties()
  const checkoutError = useCheckoutError()
  const setToast = useToast()
  const dispatch = useAppDispatch()
  const { show: shouldShowDownpaymentToast } = useLimitedViewElementControl(LimitedViewElementId.APPLE_RETAIL_DOWNPAYMENT)
  const downPaymentAmount = useDownPaymentAmount()

  useEnforcePclCardExpiryLimit()
  const { reason } = usePreferredCard() ?? {}
  const [cardExpiryMonthsEventSent, setCardExpiryMonthsEventSent] = useState<boolean>(false)

  const finaliseCheckout = useOnConfirmButtonClick()
  const isDesktop = useIsBreakpointLargeAndUp()

  usePostCheckoutTriggerExperiment()

  const isSummaryButtonDisabled = (!ready || clicked || selectedPaymentMethod === undefined || notification?.severe) ?? false

  const analyticsFirstPaymentType = firstPaymentType !== undefined && firstPaymentType !== '' ? firstPaymentType : FirstPaymentType.PaymentUpfront

  const handleConfirmClick = async (): Promise<void> => {
    if (isSummaryButtonDisabled) {
      logEvent(TrackingEvent.PRESSED_SUMMARY_PAGE_CONFIRM_BUTTON_DISABLED)
      return
    }

    setClicked(true)
    logEvent(TrackingEvent.PRESSED_SUMMARY_CONFIRM_BUTTON, {
      paymentMethodType: PaymentMethodTypes[selectedPaymentMethod?.type as PaymentMethodType],
    })
    await finaliseCheckout()
  }

  const pageTitle = `${t('summary:reviewOrder')} | ${t('common:brandName')}`

  useEffect(() => {
    if (applepay.status === ApplePayStatus.Cancelled) {
      setClicked(false)
    }
  }, [applepay.status])

  useEffect(() => {
    if (selectedPaymentMethod?.type === PaymentMethodType.CREDIT_CARD && reason === 'cardExpiryLimitInMonths' && !cardExpiryMonthsEventSent) {
      logEvent(TrackingEvent.VIEWED_NON_TERMINAL_ERROR, { errorCode: NonTerminalErrors.ClCardExpiryError })
      setCardExpiryMonthsEventSent(true)
    }
  }, [reason, selectedPaymentMethod, cardExpiryMonthsEventSent])

  useEffect(() => {
    if (downPaymentAmount > 0 && shouldShowDownpaymentToast) {
      setToast({
        message: () => (
          <>
            <ToastItem.Headline testNameSpace='apple-retail-downpayment-toast-title'>{t('summary:appleRetail.downpaymentToast.title')}</ToastItem.Headline>
            <ToastItem.Body testNameSpace='apple-retail-downpayment-toast-body'>{t('summary:appleRetail.downpaymentToast.body')}</ToastItem.Body>
          </>
        ),
        onShow: () => {
          dispatch(addElementView({ id: LimitedViewElementId.APPLE_RETAIL_DOWNPAYMENT }))
        },
        icon: 'Info',
        toastTime: 10000,
        testNameSpace: 'apple-retail-downpayment-toast',
      })
    }
  }, [downPaymentAmount, shouldShowDownpaymentToast])

  useEffect(() => {
    if (status >= CheckoutStatus.Started && !ready) {
      logEvent(TrackingEvent.CHECKOUT_CONFIRM_DISABLED, {
        checkoutStatus: status,
        checkoutError: checkoutError?.error,
      })
    }
  }, [ready])

  return (
    <SummaryRouteGuard>
      <NoErrorStatusGuard>
        <BiometricRegistrationModal />
        <Head title={pageTitle} />
        <StartedStatusGuard>
          <Header />
          <DetailedItemList />
          <PaymentMethod />
          <Summary />
          <DockedFooterPortal>
            <div className={isDesktop ? styles.desktopCta : undefined}>
              <Button
                loading={clicked}
                disabled={isSummaryButtonDisabled}
                size={'Large'}
                padding='Fluid'
                data-dd-action-name='Confirm Checkout'
                onClick={handleConfirmClick}
              >
                {t('summary:cta')}
              </Button>
            </div>
          </DockedFooterPortal>

          <TrackPageViewed eventProperties={{ firstPaymentType: analyticsFirstPaymentType, summaryPageVersion: 'ga launch v2' }} />
          {clicked && <DisabledModal />}
        </StartedStatusGuard>
      </NoErrorStatusGuard>
    </SummaryRouteGuard>
  )
}
